// MACHINERY V2 (Stroje) — the katalog CHROME below 1280 (M2.16c).
//
// The counterpart to the desktop breadcrumb + title + tabs band, and the
// katalog's answer to fleet/FleetMobileChrome.tsx. Below 1280 CatalogFilterRail
// does not render AT ALL (CatalogShell owns that branch) and pattern B absorbs
// what the rail and the title band used to carry.
//
//   page head   -> CatalogMobileHeader   (title · N modelov · readiness legend)
//   row 1       -> SearchFieldB          (full width — see below)
//   row 2       -> FiltreButton + the category chips, in ONE ChipScroller
//   row 3       -> ActiveFilterChips     (only when filters are set)
//   the rest    -> CatalogFilterSheet    (the rail's eight sections)
//
// ============================================================
//  PATTERN B, CATALOG VARIANT — WHY THIS IS NOT `FilterBarB`
// ============================================================
// The fleet puts search and Filtre on ONE row (01-filter-b-chosen.png). Board
// 16 splits them: search alone on row 1, Filtre sharing row 2 with the category
// chips (Vsetky / Frezovanie / Sustruzenie / …). Same two controls, different
// arrangement — so the CONTROLS were extracted to mobile/filterBarAtoms.tsx at
// this sub-phase and both surfaces compose them. There is exactly one search
// field and one Filtre button in the module. M2.16a's own review had to delete
// two "slightly different" copies of its atoms; this is that lesson applied
// before the copy exists rather than after.
//
// >>> THE CATEGORY TABS ARE THE EXISTING COMPONENT'S DATA, NOT ITS MARKUP. <<<
// `CatalogCategoryTabsV2` renders a WRAPPING row of 32px pills, which is a
// desktop control twice over: it wraps (the Standards block says page chip rows
// scroll with a right-edge fade and NEVER wrap) and it is 12px under the touch
// floor. Its LABEL MAP is the part worth sharing, so it is exported and reused
// here; the row itself is TouchChip inside ChipScroller — the same two kit
// primitives every other chip row in the module is built from.
//
// >>> ONE FILTER STATE, NOT A PARALLEL ONE. <<< Every control here drives the
// SAME `CatalogBrowseQuery` the rail drives. Rotate a tablet across 1280 and the
// rail comes back holding exactly what the sheet set.
//
// >>> AND THE PENDING STATE IS NOT OWNED HERE, EITHER (review finding F4). <<<
// `sheetOpen` and the staged draft used to live in this component, which
// CatalogShell unmounts at 1280 — so the rotation the paragraph above promises
// survives ate every pending selection on the way through. Both are now the
// page's and arrive as props.
//
// >>> THE CATEGORY CHIPS ARE A GROUP; THE Filtre BUTTON IS NOT ONE OF THEM
// (review item 7). <<< The desktop control carries `role="group"` +
// `aria-label` (CatalogCategoryTabsV2.tsx:104-105) and this row shipped with
// neither, while a dialog TRIGGER sat as the first sibling among `aria-pressed`
// category chips — so a screen-reader user met an unnamed run of toggles whose
// first member is not a toggle at all. The chips now carry the desktop's own
// group and label, and the Filtre button sits OUTSIDE it while staying inside
// the same ChipScroller, which is what the board draws.
//
// >>> SEARCH GETS THE SAME CLEAR AFFORDANCE THE DESKTOP BOX HAS (review item
// 4). <<< Search is deliberately not a chip (catalogActiveFilters.ts:13-21), so
// `ActiveFilterChips` renders nothing for it: without a clear button a search
// that returns zero results had no exit but select-all-delete. `clearSearch` is
// the SAME one-path clear the desktop box uses (CatalogShell.tsx's
// `onClear={c.clearSearch}`), which is what cancels the pending 300 ms debounce
// as well as emptying the box — see useCatalogBrowse.ts.
//
// THE BADGE AND THE CHIPS ARE THE SAME LIST — `chips.length` is the Filtre
// badge's number, so the two can never disagree (catalogActiveFilters.ts
// explains why that number is not `catalogActiveFilterCount`).
//
// IT FETCHES NOTHING. Like CatalogShell, this renders what the page's hook
// already holds and calls back into it.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import ActiveFilterChips from "../mobile/ActiveFilterChips";
import ChipScroller from "../mobile/ChipScroller";
import TouchChip from "../mobile/TouchChip";
import { FiltreButton, SearchFieldB } from "../mobile/filterBarAtoms";
import CatalogFilterSheet from "./CatalogFilterSheet";
import CatalogMobileHeader from "./CatalogMobileHeader";
import { CATALOG_TAB_LABEL_KEYS } from "./CatalogCategoryTabsV2";
import { CatalogFacetModel } from "./catalogFacets";
import { useCatalogActiveFilters } from "./catalogActiveFilters";
import { CatalogFilterDraftApi } from "./useCatalogFilterDraft";
import useCatalogBrowse from "./useCatalogBrowse";

export interface CatalogMobileChromeProps {
  c: ReturnType<typeof useCatalogBrowse>;
  facets: CatalogFacetModel;
  onSelectCategory: (code?: string) => void;
  onOpenLegend: () => void;
  /** Page-owned so a rotation past 1280 cannot close the sheet — see (F4). */
  sheetOpen: boolean;
  onSheetOpenChange: (open: boolean) => void;
  /** Page-owned staged draft, for the same reason. */
  filterApi: CatalogFilterDraftApi;
}

const CatalogMobileChrome: React.FC<CatalogMobileChromeProps> = ({
  c,
  facets,
  onSelectCategory,
  onOpenLegend,
  sheetOpen,
  onSheetOpenChange,
  filterApi,
}) => {
  const { translate } = useTranslation("myMachinery");
  const { chips, clearAll } = useCatalogActiveFilters(c);

  const selectedCode = c.query.categoryCode;
  const allCount = c.categories.reduce((sum, cat) => sum + cat.modelCount, 0);

  return (
    <>
      <CatalogMobileHeader
        totalCount={c.browseTotalCount}
        onOpenLegend={onOpenLegend}
      />

      <div style={{ padding: "0 14px", flexShrink: 0, display: "flex" }}>
        <SearchFieldB
          value={c.searchValue}
          onChange={c.onSearchChange}
          onSubmit={c.onSearchSubmit}
          // The desktop box's own clear, wired to the same single clear path.
          onClear={c.clearSearch}
          placeholder={translate("myMachinery:searchCatalogModels")}
        />
      </div>

      {/* `paddingBlock` is NOT decoration here: the Filtre badge sits at
          `top: -5` and ChipScroller's `overflow-x: auto` forces the block axis
          to `auto` too, so without this room the badge loses its top edge.
          8 > 5. See filterBarAtoms.tsx's header. */}
      <ChipScroller paddingBlock={8}>
        <FiltreButton
          pill
          activeCount={chips.length}
          onClick={() => onSheetOpenChange(true)}
        />

        {/* A REAL flex box, not `display: contents`: that value has a history of
            dropping the element from the accessibility tree in exactly the
            browsers this row targets, which would make the group it exists to
            declare invisible. Its own `gap: 6` reproduces ChipScroller's, and
            `flexShrink: 0` keeps the run at max-content width so the row still
            scrolls rather than squashing. The label is the desktop control's,
            from the same key (CatalogCategoryTabsV2.tsx:105). */}
        <div
          role="group"
          aria-label={translate("myMachinery:filterBy")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <TouchChip
            active={!selectedCode}
            ariaPressed={!selectedCode}
            count={allCount}
            onClick={() => onSelectCategory(undefined)}
          >
            {translate("myMachinery:allCategories")}
          </TouchChip>

          {c.categories.map((cat) => {
            const key = CATALOG_TAB_LABEL_KEYS[cat.code];
            return (
              <TouchChip
                key={cat.code}
                active={selectedCode === cat.code}
                ariaPressed={selectedCode === cat.code}
                count={cat.modelCount}
                onClick={() => onSelectCategory(cat.code)}
              >
                {/* Falls back to the server's `name` for an unmapped code — the
                    same rule the desktop tabs keep. */}
                {key ? translate(key) : cat.name}
              </TouchChip>
            );
          })}
        </div>
      </ChipScroller>

      <ActiveFilterChips chips={chips} onClearAll={clearAll} />

      {/* Mounted whether or not it is open: Radix renders nothing while closed.
          The draft no longer depends on that — it is the PAGE's now (F4) — but a
          persistent mount still avoids remounting the sheet mid-gesture. */}
      <CatalogFilterSheet
        open={sheetOpen}
        onOpenChange={onSheetOpenChange}
        c={c}
        facets={facets}
        api={filterApi}
      />
    </>
  );
};

export default CatalogMobileChrome;
